'use client';

import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Facebook, Twitter, Instagram, Linkedin, ArrowUp, Github, Youtube, Send } from 'lucide-react';
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';

// Interface for particle properties
interface Particle {
  width: number;
  height: number;
  top: string;
  left: string;
  opacity: number;
  duration: number;
}

export default function Footer() {
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const currentYear = new Date().getFullYear();
  
  // State for particles
  const [particles, setParticles] = useState<Particle[]>([]);
  
  // Generate particles only on client-side
  useEffect(() => {
    const newParticles = Array.from({ length: 20 }).map(() => ({
      width: Math.random() * 4 + 1,
      height: Math.random() * 4 + 1,
      top: `${Math.random() * 100}%`,
      left: `${Math.random() * 100}%`,
      opacity: Math.random() * 0.3,
      duration: Math.random() * 10 + 10
    }));
    
    setParticles(newParticles);
  }, []);

  return (
    <footer className="relative overflow-hidden">
      {/* Gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-900 to-primary-dark/30"></div>
      
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5 [background-image:url(/assets/hero-pattern.svg)] bg-[length:600px_600px]"></div>
      
      {/* Animated particles */}
      <div className="absolute inset-0 overflow-hidden">
        {particles.map((particle, i) => (
          <motion.div
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              width: particle.width,
              height: particle.height,
              top: particle.top,
              left: particle.left,
              opacity: particle.opacity,
            }}
            animate={{
              y: [0, -100],
              opacity: [0, 0.5, 0],
            }}
            transition={{
              duration: particle.duration,
              repeat: Infinity,
              ease: "linear",
            }}
          />
        ))}
      </div>
      
      {/* Scroll to top button */}
      <div className="absolute right-8 top-8">
        <motion.button
          onClick={scrollToTop}
          aria-label="Scroll to top"
          className="bg-gradient-rainbow text-white p-3 rounded-full shadow-neon transition-all duration-300"
          whileHover={{ y: -5, boxShadow: '0 0 20px rgba(90, 120, 255, 0.7)' }}
          whileTap={{ scale: 0.95 }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <ArrowUp className="h-5 w-5" />
        </motion.button>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Logo and About */}
          <div className="col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Link to="/" className="flex items-center mb-4">
                <img
                  src="/images/logo-white.png"
                  alt="1 Dollar Cloud"
                  width={180}
                  height={40}
                  className="h-10 w-auto"
                />
              </Link>
              <p className="text-slate-300 text-sm mt-6 leading-relaxed">
                1 Dollar Cloud provides affordable and reliable web hosting solutions for businesses of all sizes. Our mission is to make quality hosting accessible to everyone.
              </p>
              <div className="flex space-x-4 mt-8">
                {[
                  { icon: <Twitter />, href: "https://x.com/1dollarcloud", color: "bg-sky-500 hover:bg-sky-600", label: "Follow us on X (Twitter)" },
                  { icon: <Linkedin />, href: "https://www.linkedin.com/company/1-dollar-cloud", color: "bg-blue-600 hover:bg-blue-700", label: "Connect with us on LinkedIn" },
                ].map((social, index) => (
                  <motion.a
                    key={index}
                    href={social.href}
                    aria-label={social.label}
                    className={`${social.color} text-white p-2 rounded-full transition-colors duration-300 flex items-center justify-center`}
                    whileHover={{ y: -3, scale: 1.1 }}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    {social.icon}
                  </motion.a>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Services */}
          <div className="col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <h2 className="text-lg font-semibold mb-6 text-white relative inline-block">
                Hosting Services
                <motion.span 
                  className="absolute -bottom-2 left-0 w-12 h-1 bg-gradient-rainbow rounded-full"
                  animate={{ width: ["0%", "100%", "12%"] }}
                  transition={{ duration: 1.5, ease: "easeInOut" }}
                ></motion.span>
              </h2>
              <ul className="space-y-3 mt-6">
                {[
                  { name: "Shared Hosting", href: "/hosting/shared-hosting" },
                  { name: "WordPress Hosting", href: "/hosting/wordpress-hosting" },
                  { name: "VPS Hosting", href: "/hosting/vps-hosting" },
                  { name: "Dedicated Server", href: "/hosting/dedicated-server" },
                  { name: "Domain Registration", href: "/domains" },
                ].map((item, index) => (
                  <motion.li key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.2 + (index * 0.1) }}
                  >
                    <motion.div whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
                      <Link to={item.href} className="text-slate-300 hover:text-primary-light text-sm transition-colors duration-300 flex items-center">
                        <span className="mr-2 text-primary-light">›</span> {item.name}
                      </Link>
                    </motion.div>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Company */}
          <div className="col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h2 className="text-lg font-semibold mb-6 text-white relative inline-block">
                Company
                <motion.span 
                  className="absolute -bottom-2 left-0 w-12 h-1 bg-gradient-rainbow rounded-full"
                  animate={{ width: ["0%", "100%", "12%"] }}
                  transition={{ duration: 1.5, ease: "easeInOut" }}
                ></motion.span>
              </h2>
              <ul className="space-y-3 mt-6">
                {[
                  { name: "About Us", href: "/about" },
                  { name: "Blog", href: "/blog" },
                  { name: "Contact Us", href: "/contact" },
                  { name: "Privacy Policy", href: "/privacy-policy" },
                  { name: "Terms of Service", href: "/terms-of-services" },
                ].map((item, index) => (
                  <motion.li key={item.name}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3, delay: 0.3 + (index * 0.1) }}
                  >
                    <motion.div whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
                      <Link to={item.href} className="text-slate-300 hover:text-primary-light text-sm transition-colors duration-300 flex items-center">
                        <span className="mr-2 text-primary-light">›</span> {item.name}
                      </Link>
                    </motion.div>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
          </div>

          {/* Contact */}
          <div className="col-span-1">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <h2 className="text-lg font-semibold mb-6 text-white relative inline-block">
                Contact Us
                <motion.span 
                  className="absolute -bottom-2 left-0 w-12 h-1 bg-gradient-rainbow rounded-full"
                  animate={{ width: ["0%", "100%", "12%"] }}
                  transition={{ duration: 1.5, ease: "easeInOut" }}
                ></motion.span>
              </h2>
              <ul className="space-y-5 mt-6">
                <motion.li 
                  className="flex items-start"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.4 }}
                >
                  <motion.div whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
                    <div className="flex items-start">
                      <div className="bg-gradient-to-br from-primary/20 to-primary/10 p-2 rounded-full mr-4">
                        <MapPin className="h-5 w-5 text-primary-light" />
                      </div>
                      <span className="text-slate-300 text-sm">
                      66 West Flagler Street Miami Florida 33130 USA
                      </span>
                    </div>
                  </motion.div>
                </motion.li>
                <motion.li 
                  className="flex items-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.5 }}
                >
                  
                </motion.li>
                <motion.li 
                  className="flex items-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.6 }}
                >
                  <motion.div whileHover={{ x: 5 }} transition={{ duration: 0.2 }}>
                    <div className="flex items-center">
                      <div className="bg-gradient-to-br from-primary/20 to-primary/10 p-2 rounded-full mr-4">
                        <Mail className="h-5 w-5 text-primary-light" />
                      </div>
                      <a href="mailto:support@1dollarcloud.com" className="text-slate-300 hover:text-primary-light text-sm transition-colors duration-300">
                        support@1dollarcloud.com
                      </a>
                    </div>
                  </motion.div>
                </motion.li>
              </ul>
            </motion.div>
          </div>
        </div>
        
        <motion.div 
          className="border-t border-slate-800/50 mt-16 pt-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
        >
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-slate-400 text-sm">
              &copy; {currentYear} <a href="https://1dollarcloud.com" className="hover:text-primary-light transition-colors duration-300">1 Dollar Cloud</a> is a project of <a href="https://globaladsmedia.us" className="hover:text-primary-light transition-colors duration-300">Global Ads Media, LLC.</a> • All Rights Reserved
            </p>
            <div className="mt-6 md:mt-0">
              <ul className="flex space-x-8">
                <motion.li whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
                  <Link to="/privacy-policy" className="text-slate-300 hover:text-primary-light text-sm transition-colors duration-300">
                    Privacy Policy
                  </Link>
                </motion.li>
                <motion.li whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
                  <Link to="/terms-of-services" className="text-slate-300 hover:text-primary-light text-sm transition-colors duration-300">
                    Terms of Services
                  </Link>
                </motion.li>
                <motion.li whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
                  <Link to="/refund-policy" className="text-slate-300 hover:text-primary-light text-sm transition-colors duration-300">
                    Refund Policy
                  </Link>
                </motion.li>
                <motion.li whileHover={{ y: -2 }} transition={{ duration: 0.2 }}>
                  <Link to="/acceptable-use-policy" className="text-slate-300 hover:text-primary-light text-sm transition-colors duration-300">
                    Acceptable Use Policy
                  </Link>
                </motion.li>
              </ul>
            </div>
          </div>
        </motion.div>
      </div>
    </footer>
  );
}
